import {RecommendationContext} from '@/store/state/global.types';
import {
  blackListRecommendations,
  getAmexSolution,
  getSolutionRecommendations,
  isRecommendationsReady,
  removePOIFromSolution,
} from '@/store/state/helpers';
import {Activity, CandidatePoiItem, UserActivityCategoryEnum} from '@mobi-components/global-solution';
import {computed, onBeforeUnmount, onMounted, ref} from '@vue/composition-api';
import {PoiItem} from '@mobi-components/amex-poi-details-view';
import {mapToPoiItem} from '../../explore-page/details-view/mapToPoiItem';
import {getUserId} from '@/services/auth-shared';
import {trackDetailsView} from '@/store/api/trackBtnClick';
import {useParams} from '@/router/router';

export function useRecommendationDetails(addManyPois: (items: RecommendationContext[]) => Promise<void>, root: Vue) {
  const filterValue = ref('All');
  const selectedRecommendationId = ref('');

  const solutionRecommendations = computed<Activity[]>(() => {
    return getSolutionRecommendations(filterValue.value);
  });
  const solutionRecommendationsCandidates = computed<CandidatePoiItem[]>(() => {
    const params = useParams();
    const recommendations = getSolutionRecommendations(filterValue.value)
      // @ts-ignore
      .filter((x) => x.source.content && x.spec?.context === params.value.contextName)
      .map((x) => x.source.content) as CandidatePoiItem[];
    return recommendations;
  });
  const solutionRecommendationsPois = computed<PoiItem[]>(() => {
    return solutionRecommendationsCandidates.value
      .map((x) => {
        return mapToPoiItem({...x, description: (x.explanations || []).join(', ')});
      })
      .filter(Boolean) as PoiItem[];
  });

  const solutionRecommendationsAllCandidates = computed<CandidatePoiItem[]>(() => {
    return getSolutionRecommendations().map((x) => x.source.content as CandidatePoiItem);
  });
  const isRecommendationsEnabled = computed<boolean>(() => {
    return isRecommendationsReady(); // && !isAdditionalInputForRecommendationsRequired();
  });

  const selectedRecommendation = computed<RecommendationContext | null>(() => {
    const params = useParams();
    const poi = solutionRecommendations.value.find((x) => x.source?.content?.id === params.value.poiId) || null;
    if (!poi) {
      return null;
    }

    return ({
      ...poi.source.content,
      // @ts-ignore
      start_timestamp: poi.original_start_time,
      // @ts-ignore
      end_timestamp: poi.original_end_time,
    } as unknown) as RecommendationContext;
  });

  const selectedRecommendationPoi = computed(() => {
    const poi = selectedRecommendation.value || undefined;
    return mapToPoiItem(poi && {...poi, description: poi?.description || (poi?.explanations || []).join(', ')});
  });

  const solution = computed(() => {
    return getAmexSolution();
  });

  const trackView = () => {
    const userId = getUserId();
    const poi = selectedRecommendationPoi.value;
    const category = (poi?.categories?.length ? poi.categories : [UserActivityCategoryEnum.NONE])[0];
    if (poi?.id && selectedRecommendationId.value) {
      trackDetailsView(userId, category, poi.id, solution.value?.problem_id);
    }
  };

  trackView();

  const isRecommendationDetailsVisible = computed(() => {
    return Boolean(selectedRecommendationId.value);
  });

  const onAddAll = async () => {
    const recommendations = solutionRecommendations.value.map((poi) => {
      return ({
        ...poi.source.content,
        // @ts-ignore
        start_timestamp: poi.original_start_time * 1000,
        // @ts-ignore
        end_timestamp: poi.original_end_time * 1000,
      } as unknown) as RecommendationContext;
    });
    if (addManyPois) {
      await addManyPois(recommendations);
    }
    onShowRecommendation('');
  };

  const onNotInterested = async (poiId: string) => {
    const activity = solutionRecommendations.value.find((x) => x.source?.candidate_id === poiId);
    if (activity) {
      await blackListRecommendations(poiId);
      removePOIFromSolution(activity.id);
      const first = solutionRecommendations.value.find(Boolean);
      const index = solutionRecommendations.value.findIndex((x) => x.source.candidate_id === poiId);
      const prev = solutionRecommendations.value[index - 1];
      const next = solutionRecommendations.value[index + 1];
      onShowRecommendation(prev?.source?.content?.id || next?.source?.content?.id || first?.source.content?.id || '');
    }
  };

  const onShowRecommendation = (id: string) => {
    selectedRecommendationId.value = id;
    trackView();
  };

  onMounted(() => {
    root.$on('select-recommendation', onShowRecommendation);
  });

  onBeforeUnmount(() => {
    root.$off('select-recommendation', onShowRecommendation);
  });

  return {
    selectedRecommendationId,
    isRecommendationDetailsVisible,
    onShowRecommendation,
    selectedRecommendationPoi,
    solutionRecommendations,
    solutionRecommendationsCandidates,
    solutionRecommendationsAllCandidates,
    onAddAll,
    isRecommendationsEnabled,
    onNotInterested,
    solutionRecommendationsPois,
  };
}
